/**
 * The 10-second guillotine (SPEC §2.3 phase 3), which the deck reduces to a
 * strip of chrome at the top of the prediction screen (DESIGN.md §3.5).
 *
 * THE SERVER ALARM IS THE AUTHORITY. This renders the server's countdown and
 * nothing else: it derives the remaining time from `endsAt` on the *server*
 * clock, corrected by `serverOffsetMs`, and it never advances a phase. If this
 * display hits zero a beat before or after the Durable Object's alarm fires, the
 * DO still decides who scored.
 *
 * What zero *does* own on the client is the hard lock. `expired` goes true on
 * this phone's own reading of the server clock rather than waiting for
 * `predictions_locked` to arrive, so a slow socket can never buy anyone an extra
 * frame of editing. Erring early costs nothing — the server already holds the
 * last complete pair it was sent — while erring late would let a phone change an
 * answer the room has already been told is final.
 */
import { useEffect, useState } from 'react';
import { CountdownStrip } from './ui.js';

/** Exported so tests and screens can agree without duplicating the arithmetic. */
export function remainingMs(endsAt: number, serverOffsetMs: number, now: number): number {
  return Math.max(0, endsAt - (now + serverOffsetMs));
}

export interface CountdownState {
  remaining: number;
  seconds: number;
  expired: boolean;
}

export function useCountdown({
  endsAt,
  serverOffsetMs = 0,
}: {
  /** ms epoch on the SERVER clock (`lock_countdown.endsAt`). */
  endsAt: number;
  /** serverNow − clientNow at the time the frame arrived. Add to `Date.now()`. */
  serverOffsetMs?: number;
}): CountdownState {
  const [remaining, setRemaining] = useState(() =>
    remainingMs(endsAt, serverOffsetMs, Date.now()),
  );

  useEffect(() => {
    let frame = 0;
    const tick = () => {
      const left = remainingMs(endsAt, serverOffsetMs, Date.now());
      setRemaining(left);
      if (left <= 0) return;
      frame = window.setTimeout(tick, 80);
    };
    tick();
    return () => window.clearTimeout(frame);
  }, [endsAt, serverOffsetMs]);

  return { remaining, seconds: Math.ceil(remaining / 1000), expired: remaining <= 0 };
}

export type CountdownProps = CountdownState;

/**
 * The strip itself. Amber and countable while the answers can still change, ink
 * and final once they cannot — the same 44px box either way, because the deck's
 * whole point about the timer is that nothing beneath it moves on the way to
 * zero.
 */
export function Countdown({ seconds, expired }: CountdownProps) {
  const label = expired
    ? 'Answers locked'
    : `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
  return (
    <CountdownStrip tone={expired ? 'locked' : 'running'}>
      {/*
        The strip is a polite live region, and a number that changes every second
        would have a screen reader counting out loud over the room. The ticking
        half is hidden from assistive tech; what is announced is the state
        change, which is the part that matters.
      */}
      <span aria-hidden="true">{label}</span>
      <span className="sr-only">
        {expired ? 'Answers are locked.' : 'Answers lock when time runs out.'}
      </span>
    </CountdownStrip>
  );
}
